import { Inject, Injectable } from '@angular/core';
import {
	API_BASE_URL_BACKEND,
	DcBaseInitializationService,
	DcCoreBackendBasePath,
} from '@datachain/ui-sdk/common';
import { OrderedMap } from 'immutable';

import { TagAdapter } from '../core/adapters/tag.adapter';
import { DcDxGrids } from '../core/dx-types';
import { DxListsStorage } from '../core/services/dx-lists.storage';
import { ProjectStorage } from '../core/services/project.storage';
import { IConnectorResponse } from '../requesters/connector/connector.model';
import { ConnectorItemEntity } from './components/connector-list/connector-item.entity';
import {
	ConnectorListEntity,
	IConnectorListExtra,
} from './components/connector-list/connector-list.entity';

@Injectable()
export class ConnectorAdapter {
	public constructor(
		private readonly tagAdapter: TagAdapter,
		private readonly userSettings: ProjectStorage,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath,
		private readonly dxListsStorage: DxListsStorage
	) {}
	public generateConnectorList(
		response: ReadonlyArray<IConnectorResponse>
	): ConnectorListEntity {
		let availableCols: Array<IConnectorListExtra> = [
			{
				field: 'isActive',
				alias: $localize`:i18n=@@general.status:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 0,
			},
			{
				field: 'code',
				alias: $localize`:i18n=@@connector.list.column.code:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 1,
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@connector.list.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@connector.list.column.tags:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 4,
			},
			{
				field: 'type',
				alias: $localize`:i18n=@@connector.list.column.type:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 5,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@connector.list.column.description:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 6,
			},
			{
				field: 'isImported',
				alias: $localize`:i18n=@@connector.list.column.imported:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 7,
				visible: false,
			},
			{
				field: 'depotsCount',
				alias: $localize`:i18n=@@connector.list.column.depotsCount:`,
				type: 'number',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 8,
			},

			{
				field: 'creationDate',
				alias: $localize`:i18n=@@connector.list.column.creationDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 9,
			},
			{
				field: 'createdByAvatar',
				alias: $localize`:i18n=@@connector.list.column.createdBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 10,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@connector.list.column.updateDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 11,
			},
			{
				field: 'updatedByAvatar',
				alias: $localize`:i18n=@@connector.list.column.updatedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 12,
			},
			{
				field: 'mode',
				alias: $localize`:i18n=@@connector.list.column.mode:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 13,
			},
		];
		availableCols = this.dxListsStorage.synchronize<
			IConnectorListExtra,
			ConnectorItemEntity
		>(DcDxGrids.ConnectorListStandalone, availableCols);
		const list = ConnectorListEntity.build(availableCols);

		const items = response
			.reduce<Array<ConnectorItemEntity>>((acc, item) => {
				const temp = this.generateConnectorItemEntity(item);
				acc.push(temp);
				return acc;
			}, [])
			.sort((a, b) => {
				if (!a.updateDate) {
					throw new Error('Date is null!');
				}
				if (!b.updateDate) {
					throw new Error('Date is null!');
				}

				return b.updateDate.getTime() - a.updateDate.getTime();
			});

		const orderedMap = OrderedMap<string, ConnectorItemEntity>(
			items.map((item) => [`${item.id}`, item])
		);
		return list.setElements(orderedMap);
	}

	private generateConnectorItemEntity(
		response: IConnectorResponse
	): ConnectorItemEntity {
		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		return ConnectorItemEntity.build({
			id: response.id,
			label: response.label ?? '',
			code: response.code ?? '',
			description: response.description ?? '',
			isImported: response.imported,
			updateDate:
				response.update_date === undefined
					? new Date(response.creation_date ?? '')
					: new Date(response.update_date),
			creationDate: response.creation_date
				? new Date(response.creation_date)
				: null,
			updatedBy:
				response.updated_by === undefined
					? response.created_by
						? `${response.created_by.first_name} ${response.created_by.last_name}`
						: ''
					: `${response.updated_by.first_name} ${response.updated_by.last_name}`,
			createdBy: response.created_by
				? `${response.created_by.first_name} ${response.created_by.last_name}`
				: '',
			tags: this.tagAdapter.generateResponseMetadataTag(response.tags ?? []),
			isActive: response.active ?? false,
			pictogramUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.icon_id ?? -1
			}`,
			type: response.type ?? '',
			mode: response.mode ?? '',
		});
	}
}
